package com.evoapps.evoshift.viewmodels

import com.evoapps.evoshift.domain.model.Capability
import com.evoapps.evoshift.domain.model.capabilitiesAnywhere
import com.evoapps.evoshift.saves.fold
import com.evoapps.evoshift.saves.isSavedOrKept
import com.evoapps.evoshift.saves.runOrKeepOnDevice
import com.evoapps.evoshift.ui.util.UiText
import com.evoapps.evoshift.ui.util.toUiText
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.error_sign_out_failed
import evoshift.composeapp.generated.resources.profile_update_partial
import evoshift.composeapp.generated.resources.profile_updated
import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.evoapps.evoshift.domain.model.User
import com.evoapps.evoshift.domain.usecase.UpdateUserProfileUseCase
import com.evoapps.evoshift.domain.usecase.auth.GetCurrentUserUseCase
import com.evoapps.evoshift.domain.usecase.auth.DeleteAccountUseCase
import com.evoapps.evoshift.domain.usecase.auth.SignOutUseCase
import com.evoapps.evoshift.domain.usecase.owner.SharedCostChanges
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.drop
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

import com.evoapps.evoshift.domain.model.Owner
import com.evoapps.evoshift.domain.model.RecurringCostRule
import com.evoapps.evoshift.domain.usecase.GetOwnerUseCase
import com.evoapps.evoshift.domain.usecase.UpdateOwnerBusinessNameUseCase
import com.evoapps.evoshift.domain.usecase.settings.GetRecurringCostRulesUseCase
import com.evoapps.evoshift.domain.model.UserPermission
import com.evoapps.evoshift.domain.usecase.GetWorkplaceUseCase

data class ProfileUiState(
    val isLoading: Boolean = false,
    val currentUser: User? = null,
    val ownerProfile: Owner? = null,
    /** Owns a workplace (active): the plan, the business name and the recurring costs show. */
    val isAnyWorkplaceOwner: Boolean = false,
    /** What the person may do at any of their workplaces (the profile's owner sections). */
    val capabilities: Set<Capability> = emptySet(),
    val recurringCostRules: List<RecurringCostRule> = emptyList(),
    val workplaceCurrencies: Map<String, String> = emptyMap(), // id to currency
    /** SH-131: how many shared costs of the business the owner has (null: not read). */
    val sharedCostCount: Int? = null,
    val isEditing: Boolean = false, // Tryb edycji włączony/wyłączony
    /** The edit form is being saved: it stays open with its button disabled. */
    val isSaving: Boolean = false,
    val successMessage: UiText? = null,
    val error: UiText? = null,
    val isSignedOut: Boolean = false,
    /** The account is being deleted (the dialog shows progress). */
    val isDeleting: Boolean = false
) {
    /** SH-267: workplaces the person is the (active) owner of: groups matter from two on. */
    val ownedWorkplaceCount: Int
        get() = currentUser?.engagements?.values
            ?.count { it.isActive && it.permission == com.evoapps.evoshift.domain.model.UserPermission.OWNER } ?: 0
}

sealed interface ProfileEvent {
    data class UpdateProfile(
        val newName: String,
        val newBusinessName: String?
    ) : ProfileEvent
    data object ToggleEditMode : ProfileEvent
    data object Refresh : ProfileEvent
    data object MessageShown : ProfileEvent
    data object SignOut : ProfileEvent
    data object DeleteAccount : ProfileEvent
}

class ProfileViewModel(
    private val getCurrentUserUseCase: GetCurrentUserUseCase,
    private val updateUserProfileUseCase: UpdateUserProfileUseCase,
    private val signOutUseCase: SignOutUseCase,
    private val deleteAccountUseCase: DeleteAccountUseCase,
    private val getOwnerUseCase: GetOwnerUseCase,
    private val updateOwnerBusinessNameUseCase: UpdateOwnerBusinessNameUseCase,
    private val getRecurringCostRulesUseCase: GetRecurringCostRulesUseCase,
    private val getWorkplaceUseCase: GetWorkplaceUseCase,
    /** SH-131: the owner's shared costs (counted on the profile); without it none are shown. */
    private val getSharedCostsUseCase: com.evoapps.evoshift.domain.usecase.owner.GetSharedCostsUseCase? = null,
    /** SH-136: a change on the shared costs screen recounts them here (the profile stays open behind it). */
    private val sharedCostChanges: SharedCostChanges? = null,
    /** SH-148: the app's saves: a write started here finishes even if the screen is left. */
    private val saves: com.evoapps.evoshift.saves.PendingSaves = com.evoapps.evoshift.saves.PendingSaves(),
    /** SH-186: offline, a save unanswered for a few seconds counts as kept on this device. */
    private val connectivity: com.evoapps.evoshift.connectivity.ConnectivityWatcher = com.evoapps.evoshift.connectivity.ManualConnectivity()
) : ViewModel() {

    private val _uiState = MutableStateFlow(ProfileUiState(isLoading = true))
    val uiState: StateFlow<ProfileUiState> = _uiState.asStateFlow()
    // Refresh restarts the listener instead of adding a second one.
    private val userJob = JobSlot()

    init {
        // Automatycznie pobierz dane przy starcie
        observeUser()
        observeSharedCostChanges()
    }

    /** SH-136: the count of shared costs after the owner saved or deleted one. */
    private fun observeSharedCostChanges() {
        val changes = sharedCostChanges ?: return
        viewModelScope.launch {
            changes.version.drop(1).collect {
                val uid = _uiState.value.currentUser?.uid ?: return@collect
                if (!_uiState.value.isAnyWorkplaceOwner) return@collect
                getSharedCostsUseCase?.invoke(uid)?.getOrNull()?.let { costs ->
                    _uiState.update { it.copy(sharedCostCount = costs.size) }
                }
            }
        }
    }

    fun onEvent(event: ProfileEvent) {
        when (event) {
            is ProfileEvent.UpdateProfile -> updateProfile(event.newName, event.newBusinessName)
            ProfileEvent.ToggleEditMode -> {
                _uiState.update { it.copy(isEditing = !it.isEditing) }
            }

            ProfileEvent.Refresh -> observeUser()
            ProfileEvent.MessageShown -> {
                _uiState.update { it.copy(successMessage = null, error = null) }
            }
            ProfileEvent.SignOut -> performSignOut()
            ProfileEvent.DeleteAccount -> deleteAccount()
        }
    }

    private fun observeUser() {
        userJob.launch(viewModelScope) {
            getCurrentUserUseCase().collect { user ->
                val capabilities = user?.capabilitiesAnywhere.orEmpty()
                // The owner's things (owners/{uid}: plan, business name, cost rules) are read only
                // by someone who owns a workplace now.
                val isAnyWorkplaceOwner = capabilities.shows(Gate.PLAN) || capabilities.shows(Gate.RECURRING_COSTS)

                _uiState.update {
                    it.copy(
                        currentUser = user,
                        isAnyWorkplaceOwner = isAnyWorkplaceOwner,
                        capabilities = capabilities,
                        isLoading = user == null
                    )
                }
                
                if (user != null && isAnyWorkplaceOwner) {
                    loadOwnerData(user.uid)
                } else if (user != null) {
                    _uiState.update { it.copy(isLoading = false) }
                }
            }
        }
    }
    
    private suspend fun loadOwnerData(uid: String) {
        val ownerResult = getOwnerUseCase(uid)
        val owner = ownerResult.getOrNull()
        
        _uiState.update { it.copy(ownerProfile = owner) }
        
        if (owner != null) {
            val rulesResult = getRecurringCostRulesUseCase(uid)
            val rules = rulesResult.getOrNull() ?: emptyList()
            
            val currencies = mutableMapOf<String, String>()
            rules.map { it.targetWorkplaceId }.distinct().forEach { wpId ->
                getWorkplaceUseCase(wpId).onSuccess { wp ->
                    currencies[wpId] = wp.settings.currency
                }
            }
            
            val shared = getSharedCostsUseCase?.invoke(uid)?.getOrNull()?.size
            _uiState.update { it.copy(recurringCostRules = rules, workplaceCurrencies = currencies, sharedCostCount = shared, isLoading = false) }
        } else {
            _uiState.update { it.copy(isLoading = false) }
        }
    }

    private fun performSignOut() {
        viewModelScope.launch {
            try {
                signOutUseCase()
                _uiState.update { it.copy(isSignedOut = true) }
            } catch (e: Exception) {
                _uiState.update { it.copy(error = UiText.Resource(Res.string.error_sign_out_failed, e.toUiText())) }
            }
        }
    }

    /** Deletes the account on the server; signing out then returns the app to the login screen. */
    private fun deleteAccount() {
        if (_uiState.value.isDeleting) return
        _uiState.update { it.copy(isDeleting = true, error = null) }
        viewModelScope.launch {
            deleteAccountUseCase().fold(
                onSuccess = { _uiState.update { it.copy(isDeleting = false, isSignedOut = true) } },
                onFailure = { e -> _uiState.update { it.copy(isDeleting = false, error = e.toUiText()) } }
            )
        }
    }

    private fun updateProfile(newName: String, newBusinessName: String?) {
        val user = _uiState.value.currentUser ?: return
        if (_uiState.value.isSaving) return

        _uiState.update { it.copy(isSaving = true) }
        viewModelScope.launch {

            // The photo has its own flow (ProfilePhotoViewModel, SH-94).
            val updatedUser = user.copy(displayName = newName)
            val businessName = newBusinessName?.takeIf { _uiState.value.ownerProfile != null }

            // SH-148: both writes in the app's saves; SH-186: offline, they sit in this device's
            // cache: after a few seconds the form closes and the shell says so.
            val outcome = saves.runOrKeepOnDevice("profile:${user.uid}", connectivity.isOnline, queue = true) {
                updateUserProfileUseCase(updatedUser).mapCatching {
                    if (businessName != null) updateOwnerBusinessNameUseCase(user.uid, businessName).getOrThrow()
                }
            }
            // Only the name changed: no need to re-read the owner, rules and currencies.
            if (businessName != null && outcome.isSavedOrKept) {
                _uiState.update { it.copy(ownerProfile = it.ownerProfile?.copy(businessName = businessName)) }
            }
            outcome.fold(
                onSuccess = {
                    _uiState.update {
                        it.copy(
                            isSaving = false,
                            isEditing = false,
                            successMessage = UiText.Resource(Res.string.profile_updated)
                        )
                    }
                },
                onFailure = {
                    _uiState.update {
                        it.copy(isSaving = false, error = UiText.Resource(Res.string.profile_update_partial))
                    }
                },
                onKept = { _uiState.update { it.copy(isSaving = false, isEditing = false) } }
            )
        }
    }
}